import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/Shell";
import api, { formatError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/utils/checkout";
import { userHas } from "@/utils/entitlements";
import { toast } from "sonner";
import {
  Languages, FileText, Upload, Download, Trash2, RefreshCw,
  CheckCircle2, AlertCircle, Loader2, KeyRound, Lock, Sparkles,
} from "lucide-react";
import { trackEvent } from "@/hooks/useAnalytics";

export default function PubGlotPage() {
  const { user } = useAuth();
  const [hasKey, setHasKey] = useState(null);
  const [langs, setLangs] = useState([]);
  const [targetLang, setTargetLang] = useState("Spanish");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    if (userHas("pubglot", user)) {
      trackEvent({ type: "tool_launch", tool: "pubglot", path: "/tools/pubglot" });
    }
  }, [user]);

  // ---- File handling helpers (shared by click-upload + drag-drop) ----
  const ALLOWED_EXT = [".docx", ".pdf", ".epub"];
  const handleFiles = (files) => {
    if (!files || !files.length) return;
    const f = files[0];
    const lower = (f.name || "").toLowerCase();
    if (!ALLOWED_EXT.some((ext) => lower.endsWith(ext))) {
      toast.error("Only .docx, .pdf, or .epub files are supported");
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error("File is over the 50MB limit");
      return;
    }
    setFile(f);
  };
  const onDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer?.files);
  };
  const [history, setHistory] = useState([]);
  const timerRef = useRef(null);
  const hasPubglot = userHas("pubglot", user);

  const loadAll = async () => {
    try {
      const [kd, ld, hd] = await Promise.all([
        api.get("/api/settings/anthropic-key"),
        api.get("/api/pubglot/languages"),
        api.get("/api/pubglot/history"),
      ]);
      setHasKey(kd.data.has_anthropic_key);
      setLangs(ld.data.languages);
      setHistory(hd.data);
    } catch (e) {}
  };

  useEffect(() => {
    loadAll();
    timerRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/api/pubglot/history");
        setHistory(data);
      } catch (e) {}
    }, 2000);
    return () => clearInterval(timerRef.current);
  }, []);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("target_language", targetLang);
      const { data } = await api.post("/api/pubglot/upload", fd);
      await api.post(`/api/pubglot/translate/${data.record_id}`);
      toast.success("Translation started");
      setFile(null);
      loadAll();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const download = async (id, type = "translated", name = "file") => {
    try {
      const r = await api.get(`/api/pubglot/download/${id}?type=${type}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"];
      const m = cd?.match(/filename="?([^"]+)"?/);
      a.download = m?.[1] || name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this translation record?")) return;
    try {
      await api.delete(`/api/pubglot/history/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const retranslate = async (id) => {
    const lang = window.prompt("Translate to which language?", "French");
    if (!lang) return;
    try {
      await api.post(`/api/pubglot/retranslate/${id}?target_language=${encodeURIComponent(lang)}`);
      toast.success("Re-translation started");
      loadAll();
    } catch (e) {
      toast.error("Failed");
    }
  };

  if (!hasPubglot) {
    const unlock = async () => {
      setBuying(true);
      try { await startCheckout("vip"); }
      catch (e) { toast.error("Could not start checkout"); setBuying(false); }
    };
    return (
      <Shell>
        <div className="vbs-page-eye">PUBGLOT · DOCUMENT TRANSLATION</div>
        <h1 className="vbs-page-h"><em>PubGlot</em> is locked</h1>
        <p className="vbs-page-p">
          PubGlot ships with Viral Book Suite VIP and the Evergreen Cohort. Upgrade once to unlock translation across 15+ languages — one-time payment, permanent access.
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={unlock} disabled={buying} className="vbs-btn vbs-btn-gold" data-testid="pubglot-unlock-vip">
            <Sparkles size={14} /> {buying ? "Loading checkout…" : "Upgrade to VIP — $997"}
          </button>
          <Link to="/#pricing" className="vbs-btn vbs-btn-ghost">See all tiers</Link>
        </div>
        <div className="vbs-card" style={{ marginTop: 30, padding: 22, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <Lock size={20} style={{ color: "var(--gold)", flexShrink: 0, marginTop: 2 }} />
          <div style={{ color: "var(--mid)", fontSize: 13.5, lineHeight: 1.7 }}>
            VIP also adds the 45-minute onboarding call with Mayowa, the complete system video, VA training video, publishing playbook, and formatting system.
          </div>
        </div>
      </Shell>
    );
  }

  if (hasKey === false) {
    return (
      <Shell>
        <div className="vbs-page-eye">PUBGLOT · DOCUMENT TRANSLATION</div>
        <h1 className="vbs-page-h">Connect your <em>Anthropic</em> key first</h1>
        <p className="vbs-page-p">PubGlot uses Claude Sonnet 4.5 to translate your documents. Add your key in Settings to begin.</p>
        <Link to="/settings" className="vbs-btn vbs-btn-gold" data-testid="pubglot-connect-key">
          <KeyRound size={14} /> Open Settings
        </Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="vbs-page-eye">PUBGLOT · DOCUMENT TRANSLATION</div>
      <h1 className="vbs-page-h">Translate <em>books</em> and documents</h1>
      <p className="vbs-page-p">
        Upload a .docx, .pdf or .epub file. PubGlot extracts text, translates it to your choosen language while preserving formatting, and returns a polished .docx.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 420px) 1fr", gap: 22 }}>
        <form onSubmit={onUpload} className="vbs-card" style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Languages size={18} style={{ color: "var(--gold)" }} />
            <div style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 700, color: "var(--white)" }}>
              New translation
            </div>
          </div>

          <label
            htmlFor="pubglot-file"
            data-testid="pubglot-dropzone"
            onDragEnter={onDrag}
            onDragOver={onDrag}
            onDragLeave={onDrag}
            onDrop={onDrop}
            style={{
              border: "1.5px dashed " + (dragActive ? "var(--gold)" : (file ? "var(--teal)" : "var(--line-2)")),
              borderRadius: 10,
              padding: "28px 18px",
              textAlign: "center",
              cursor: "pointer",
              background: dragActive
                ? "rgba(201,168,76,0.08)"
                : (file ? "rgba(45,184,145,0.05)" : "var(--deep)"),
              display: "block",
              transition: "border-color .2s, background .2s",
            }}
          >
            <FileText size={28} style={{ color: dragActive ? "var(--gold)" : (file ? "var(--teal)" : "var(--mid)"), marginBottom: 8 }} />
            <div style={{ color: "var(--bright)", fontSize: 13, fontWeight: 600 }}>
              {dragActive ? "Drop your file here" : (file ? file.name : "Click to upload — or drag & drop")}
            </div>
            <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 4 }}>
              .docx · .pdf · .epub
            </div>
            <input
              id="pubglot-file"
              type="file"
              accept=".docx,.pdf,.epub"
              onChange={(e) => handleFiles(e.target.files)}
              style={{ display: "none" }}
              data-testid="pubglot-file-input"
            />
          </label>

          <label>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--gold-dim)", letterSpacing: 1.5, marginBottom: 6, textTransform: "uppercase" }}>
              Target language
            </div>
            <select
              data-testid="pubglot-language"
              className="vbs-input"
              value={targetLang}
              onChange={(e) => setTargetLang(e.target.value)}
              style={{ appearance: "none" }}
            >
              {langs.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </label>

          <button data-testid="pubglot-start" type="submit" className="vbs-btn vbs-btn-gold" disabled={uploading || !file}>
            {uploading ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Starting…
              </>
            ) : (
              <>
                <Upload size={14} /> Start translation
              </>
            )}
          </button>
        </form>

        <div className="vbs-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--line)" }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700, color: "var(--white)" }}>
              Recent translations
            </div>
          </div>
          {history.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 }} data-testid="pubglot-empty">
              No translations yet. Upload a file to get started.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="vbs-table" data-testid="pubglot-history">
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Lang</th>
                    <th>Status</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>{h.original_filename}</td>
                      <td style={{ color: "var(--mid)" }}>{h.target_language}</td>
                      <td>
                        {h.status === "processing" || h.status === "pending" ? (
                          <div style={{ minWidth: 90 }}>
                            <div style={{ fontSize: 11, color: "var(--gold)", marginBottom: 4 }}>
                              {h.progress || 0}%
                            </div>
                            <div style={{ height: 3, background: "var(--line)", borderRadius: 2 }}>
                              <div
                                style={{
                                  width: `${h.progress || 0}%`,
                                  height: "100%",
                                  background: "var(--gold)",
                                  borderRadius: 2,
                                  transition: "width .3s",
                                }}
                              />
                            </div>
                          </div>
                        ) : h.status === "completed" ? (
                          <span className="vbs-chip ok">
                            <CheckCircle2 size={11} /> done
                          </span>
                        ) : (
                          <span className="vbs-chip err" title={h.error_message}>
                            <AlertCircle size={11} /> failed
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {h.status === "completed" && (
                          <button className="vbs-btn vbs-btn-ghost" style={{ padding: "6px 10px" }}
                                  onClick={() => download(h.id, "translated", `Translated_${h.target_language}.docx`)}
                                  data-testid={`download-${h.id}`}>
                            <Download size={13} />
                          </button>
                        )}
                        <button className="vbs-btn vbs-btn-ghost" style={{ padding: "6px 10px", marginLeft: 6 }}
                                onClick={() => retranslate(h.id)}
                                data-testid={`retranslate-${h.id}`}>
                          <RefreshCw size={13} />
                        </button>
                        <button className="vbs-btn vbs-btn-ghost" style={{ padding: "6px 10px", marginLeft: 6 }}
                                onClick={() => remove(h.id)}
                                data-testid={`delete-${h.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}
